/** One catalog item: what it is, what it will ask you for, and what happens next.
 *
 * The page a requester reads before committing. It answers the questions the
 * brief lists — required information, SLA, fulfilment time, approval, cost,
 * knowledge articles, related services — and for each one it either shows the
 * answer or says plainly that the catalog does not carry it yet.
 *
 * Three of those (SLA, fulfilment group, published state) are already columns on
 * `ServiceSubcategory` and are missing only from `PortalSubcategorySerializer`.
 * They are marked as a serializer change rather than a modelling problem,
 * because that difference is the difference between an afternoon and a sprint.
 */
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  ArrowRight,
  BadgePoundSterling,
  BookOpen,
  CircleCheck,
  CircleDashed,
  CircleMinus,
  Clock,
  LayoutGrid,
  ListChecks,
  ShieldCheck,
  Users,
} from 'lucide-react'
import { Link, useParams } from 'react-router-dom'

import { PageHeader } from '../../components/layout/AppShell'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { ErrorNote } from '../../components/ui/callout'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/primitives'
import { SchemaFieldInput } from '../../components/ui/schema-form'
import {
  WORKFLOW,
  relatedItems,
  schemaSupport,
  type CatalogEntry,
  type StageSupport,
} from '../../lib/catalog'
import type { ArticleSummary } from '../../lib/types'
import { cn } from '../../lib/utils'
import { api } from '../../lib/api'

export function CatalogItemPage() {
  const { id } = useParams<{ id: string }>()

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['catalog'],
    queryFn: () => api.get<CatalogEntry[]>('/catalog/'),
  })
  const item = items.find((entry) => String(entry.id) === id)

  // Knowledge articles matched on the item's name. A real catalog curates this
  // link; title search is the honest stand-in, and it is labelled as one.
  const { data: articles = [] } = useQuery({
    queryKey: ['kb-search', item?.name ?? ''],
    queryFn: () => api.get<ArticleSummary[]>(`/kb/?q=${encodeURIComponent(item?.name ?? '')}`),
    enabled: Boolean(item?.name),
  })

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
          <Skeleton className="h-96" />
          <Skeleton className="h-64" />
        </div>
      </div>
    )
  }

  if (!item) {
    return (
      <>
        <BackLink />
        <ErrorNote message="That service is not in your catalog. It may have been unpublished, or it may belong to another organisation." />
      </>
    )
  }

  const fields = item.form_schema?.fields ?? []
  const support = schemaSupport(fields)
  const related = relatedItems(items, item)

  return (
    <>
      <BackLink />

      <PageHeader
        title={item.name}
        description={`Part of ${item.service}.`}
        actions={
          <Button asChild size="lg">
            <Link to={`/new?item=${item.id}`}>
              Request this
              <ArrowRight />
            </Link>
          </Button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        {/* ------------------------------------------------------ main */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-1.5">
                <ListChecks className="size-3.5 text-muted-foreground" aria-hidden />
                What we will ask you for
              </CardTitle>
              {fields.length > 0 && (
                <Badge variant="neutral" size="sm">
                  {fields.length} field{fields.length === 1 ? '' : 's'}
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              {fields.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nothing beyond a summary and the details you want to give us.
                </p>
              ) : (
                <>
                  {/* A real preview of the real form, disabled. Not a list of
                      field names: what a requester wants to know is whether
                      they have the information to hand. */}
                  <div className="space-y-4 opacity-90" aria-hidden>
                    {fields.map((field) => (
                      <SchemaFieldInput
                        key={field.name}
                        field={field}
                        value=""
                        disabled
                        onChange={() => {}}
                      />
                    ))}
                  </div>
                  <p className="mt-3 border-t border-border pt-2.5 text-xs text-muted-foreground">
                    A preview — nothing here is submitted. Choose{' '}
                    <strong>Request this</strong> to fill it in.
                    {support.unsupported.length > 0 && (
                      <>
                        {' '}
                        {support.unsupported.length} field
                        {support.unsupported.length === 1 ? '' : 's'} in this form use a type the
                        portal cannot yet render and will appear as plain text.
                      </>
                    )}
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-1.5">
                <CircleDashed className="size-3.5 text-muted-foreground" aria-hidden />
                What happens after you ask
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {WORKFLOW.map((stage, index) => (
                  <li key={stage.key} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <StageMark support={stage.support} />
                      {index < WORKFLOW.length - 1 && (
                        <span className="mt-1 w-px flex-1 bg-border" aria-hidden />
                      )}
                    </div>
                    <div className="min-w-0 flex-1 pb-1">
                      <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-foreground">
                        {stage.label}
                        {stage.support !== 'backed' && (
                          <Badge
                            variant={stage.support === 'absent' ? 'neutral' : 'warning'}
                            size="sm"
                          >
                            {stage.support === 'absent' ? 'not yet' : 'partly'}
                          </Badge>
                        )}
                      </p>
                      <p className="text-xs leading-relaxed text-muted-foreground">{stage.note}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>

          {articles.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-1.5">
                  <BookOpen className="size-3.5 text-muted-foreground" aria-hidden />
                  These might answer it without a request
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {articles.slice(0, 5).map((article) => (
                    <li key={article.slug}>
                      <Link
                        to={`/kb/${article.slug}`}
                        className="text-sm text-primary underline underline-offset-2"
                      >
                        {article.title}
                      </Link>
                    </li>
                  ))}
                </ul>
                <p className="mt-2.5 border-t border-border pt-2 text-xs text-muted-foreground">
                  Matched on this service&apos;s name. A curated list needs a link between catalog
                  items and articles, which the model does not have.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* ----------------------------------------------------- aside */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Service details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3 text-xs">
                <Fact icon={LayoutGrid} label="Category" value={item.service} />
                <Unknown
                  icon={Clock}
                  label="Expected fulfilment time"
                  why="Derivable from the item's SLA policy, which is on the model but not returned by the catalog endpoint."
                />
                <Unknown
                  icon={ShieldCheck}
                  label="SLA"
                  why="ServiceSubcategory.default_sla exists. Add it to PortalSubcategorySerializer and this becomes real."
                />
                <Unknown
                  icon={Users}
                  label="Fulfilment group"
                  why="ServiceSubcategory.default_team already routes the ticket. It is simply not serialized to the portal."
                />
                <Unknown
                  icon={CircleCheck}
                  label="Approval required"
                  why="No catalog item can require approval. The approval model belongs to change requests."
                />
                <Unknown
                  icon={BadgePoundSterling}
                  label="Estimated cost"
                  why="No cost or currency field exists on a catalog item."
                />
              </dl>
            </CardContent>
          </Card>

          {related.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Related services</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {related.map((entry) => (
                    <li key={entry.id}>
                      <Link
                        to={`/catalog/${entry.id}`}
                        className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                      >
                        <ArrowRight className="size-3 shrink-0" aria-hidden />
                        <span className="truncate">{entry.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <p className="mt-2.5 border-t border-border pt-2 text-xs text-muted-foreground">
                  Others in {item.service}. A curated relationship would need its own field.
                </p>
              </CardContent>
            </Card>
          )}

          <Button asChild size="lg" className="w-full">
            <Link to={`/new?item=${item.id}`}>
              Request this
              <ArrowRight />
            </Link>
          </Button>
        </div>
      </div>
    </>
  )
}

function BackLink() {
  return (
    <Link
      to="/catalog"
      className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="size-3.5" />
      Service catalog
    </Link>
  )
}

function StageMark({ support }: { support: StageSupport }) {
  const Icon = support === 'backed' ? CircleCheck : support === 'partial' ? CircleDashed : CircleMinus
  return (
    <Icon
      className={cn(
        'size-4 shrink-0',
        support === 'backed'
          ? 'text-success'
          : support === 'partial'
            ? 'text-warning'
            : 'text-muted-foreground/50',
      )}
      aria-hidden
    />
  )
}

function Fact({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Clock
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0">
        <dt className="text-muted-foreground">{label}</dt>
        <dd className="font-medium text-foreground">{value}</dd>
      </div>
    </div>
  )
}

/** A detail the catalog is asked to show and cannot.
 *
 *  Shown rather than omitted, and with the reason attached. A requester who
 *  wants to know whether something needs approval is better served by "we
 *  cannot tell you yet" than by a silent gap they read as "no".
 */
function Unknown({
  icon: Icon,
  label,
  why,
}: {
  icon: typeof Clock
  label: string
  why: string
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground/50" aria-hidden />
      <div className="min-w-0">
        <dt className="flex items-center gap-1.5 text-muted-foreground">
          {label}
          <span className="rounded-full border border-dashed border-border px-1.5 text-[10px]">
            not set
          </span>
        </dt>
        <dd className="leading-relaxed text-muted-foreground/80">{why}</dd>
      </div>
    </div>
  )
}
